<script lang="ts" setup>
import { ContextType } from '@/graphql/types-service';
import { UserType } from '@/types';
import { delayMs } from '@/utils/time';

defineProps<{
    immutable?: boolean;
}>();
const version = computed(() => appVersion);
const commitSha = computed(() => appCommitSha);
const drawerVisible = ref(false);
const fixed = ref(false);
const session = useSessionStore();
const menu = useMenuStore();
const route = useRoute();
const router = useRouter();
const home = ref({
    icon: 'pi pi-home',
    route: '/'
});
const overviewVisible = computed(() => route.matched[0].path === '/');
const breadcrumbStore = useBreadcrumbStore();
onMounted(async () => {
    await session.init();
    await delayMs(200);
    if (!fixed.value) {
        menu.show();
    }
});
onActivated(async () => {
    await session.init();
    await delayMs(200);
    if (!fixed.value) {
        menu.show();
    }
});
const el = ref(null);
let oldWidth: number | null = null;
useResizeObserver(el, async (entries) => {
    const entry = entries[0];
    const { width } = entry.contentRect;
    const isSmall = width < 1150;
    fixed.value = isSmall;
    if (!isSmall) {
        drawerVisible.value = false;
    }
    if (oldWidth !== null && oldWidth < width && !isSmall && !menu.visible) {
        menu.show();
    }
    oldWidth = width;
});

function onClearContext() {
    session.selectedContext.clear();
    router.push('/context');
}
function onToggle() {
    if (fixed.value) {
        drawerVisible.value = !drawerVisible.value;
    } else {
        menu.toggle();
    }
}
const icon = computed(() => {
    switch (session.sessionContext.type) {
        case UserType.Admin:
            return 'pi-crown';
        case UserType.Olb:
        case UserType.OlbBranch:
        case UserType.Hdp:
            return 'pi-building';
        case UserType.Wsp:
        case UserType.WspBranch:
            return 'pi-warehouse';
        case UserType.Lab:
        case UserType.LabBranch:
            return 'pi-verified';
        default:
            return 'pi-spinner-dotted';
    }
});
</script>
<template>
    <AppLoader
        v-if="session.isLoading"
        :progress="!session.isChecking"
        :healthy="session.isHealthy"
    />
    <div v-else ref="el">
        <AppHeader
            :no-context="
                session.contextType !== ContextType.Sys ||
                session.userType === UserType.Applicant
            "
            :user-id="
                !session.sessionContext.isAdmin
                    ? session.sessionContext.userId
                    : null
            "
            :menu="menu.available"
            @logout="session.logout"
            @context="onClearContext"
            @toggle="onToggle"
        />
        <AppSidebar :visible="menu.visible && !fixed" :items="menu.items" />
        <Drawer
            v-if="fixed"
            v-model:visible="drawerVisible"
            label="nav"
            style="--p-drawer-header-padding: 1rem"
        >
            <template #header>
                <div class="flex items-center gap-2">
                    <ShapthLogo class="w-8" />
                    <span class="font-semibold text-xl">SHAPTH</span>
                </div>
            </template>
            <AppNavItem
                v-for="item in menu.items"
                :key="item.key"
                :item="item"
                @clicked="drawerVisible = false"
            />
        </Drawer>
        <div
            :class="[
                'ml-80 pt-24 pr-8 pb-8 pl-8 flex flex-col justify-between min-h-screen transition-[margin-left] duration-200',
                { 'ml-0!': !menu.visible || fixed }
            ]"
        >
            <div
                :class="[
                    'mx-auto w-full',
                    !menu.visible ? '2xl:w-376' : '4xl:w-[98rem]'
                ]"
            >
                <Breadcrumb
                    v-if="breadcrumbStore.items.length > 0"
                    class="bg-transparent"
                    :home="home"
                    :model="breadcrumbStore.items"
                >
                    <template #item="{ item, props }">
                        <ProgressSpinner
                            v-if="item.loading"
                            class="w-5 h-5 relative top-[0.2rem]"
                            stroke-width="8"
                        />
                        <router-link
                            v-else-if="item.route"
                            v-slot="{ href, navigate }"
                            :to="item.route"
                            custom
                        >
                            <a
                                :href="href"
                                v-bind="props.action"
                                @click="navigate"
                            >
                                <span :class="[item.icon, 'text-color']" />
                                <span class="text-primary font-semibold">{{
                                    item.label
                                }}</span>
                            </a>
                        </router-link>
                        <a
                            v-else
                            :href="item.url"
                            :target="item.target"
                            v-bind="props.action"
                        >
                            <span
                                class="text-surface-700 dark:text-surface-0"
                                >{{ item.label }}</span
                            >
                        </a>
                    </template>
                </Breadcrumb>
                <AdminContextMessage
                    v-if="session.sessionContext.isAdminOrSupport && !immutable"
                    class="mb-8"
                />
                <SimpleCard
                    v-else-if="session.actor && session.actor.active"
                    ref="user_context"
                    class="mb-8"
                    data-testid="user-context-greater"
                >
                    <SimpleCardTitle class="flex">
                        <div>{{ session.actor.name }}</div>
                        <div class="flex-1 text-right">
                            <Chip
                                class="bg-secondary dark:bg-primary-contrast normal-case"
                            >
                                <div class="flex">
                                    <div class="mr-2">
                                        {{ session.sessionContext.fullname }}
                                    </div>
                                    <div><span :class="`pi ${icon}`" /></div>
                                </div>
                            </Chip>
                        </div>
                    </SimpleCardTitle>
                    <Divider />
                    <div class="text-sm grid grid-cols-[auto_1fr] gap-x-8 pb-2">
                        <div class="text-muted-color">
                            {{ $t('ux.labels.id') }}
                        </div>
                        <div>{{ session.actor.id }}</div>
                        <div class="text-muted-color">
                            {{ $t('ux.labels.federal_state') }}
                        </div>
                        <div>
                            {{ $t(`dx.state.${session.federalStateKey}`) }}
                        </div>
                    </div>
                </SimpleCard>
                <!-- Display admin context -->
                <router-view />
                <!-- Display admin overview -->
                <AdminOverview
                    v-if="overviewVisible && session.isActiveAsAdmin"
                    class="mt-8"
                />
            </div>

            <div class="grow-0 text-center mt-4">
                <Divider />
                <span class="text-zinc-500 dark:text-surface-400 text-sm ml-2"
                    >SHAPTH - {{ version }} &copy; 2026</span
                >
                <div class="text-zinc-400 dark:text-surface-500 text-xs">
                    {{ commitSha }}
                </div>
            </div>
        </div>
        <AppConfig />
    </div>
</template>
